/**
 * Trusted enrollment issuers — query + mutation hooks.
 * Tenant-scoped, platform-admin only:
 *   /api/v1/tenants/{tenant_id}/trusted-enrollment-issuers
 *
 * The backend gates this on platform-admin (not a tenant permission) and does
 * NOT step-up-gate it, so — unlike the workload-issuer registry — the mutation
 * is a plain apiFetch (no withStepUp).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import {
  createTrustedEnrollmentIssuerSchema,
  trustedEnrollmentIssuerSchema,
  trustedEnrollmentIssuersResponseSchema,
  updateTrustedEnrollmentIssuerSchema,
  type CreateTrustedEnrollmentIssuerInput,
  type TrustedEnrollmentIssuer,
  type TrustedEnrollmentIssuersResponse,
  type UpdateTrustedEnrollmentIssuerInput,
} from "./schemas";

function base(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/trusted-enrollment-issuers`;
}

export async function fetchTrustedEnrollmentIssuers(
  tenantID: string,
): Promise<TrustedEnrollmentIssuersResponse> {
  const raw = await apiFetch<unknown>(base(tenantID));
  return trustedEnrollmentIssuersResponseSchema.parse(raw);
}

export function useTrustedEnrollmentIssuersQuery(tenantID: string) {
  return useQuery<TrustedEnrollmentIssuersResponse, ApiError>({
    queryKey: queryKeys.trustedEnrollmentIssuers.list(tenantID),
    queryFn: () => fetchTrustedEnrollmentIssuers(tenantID),
  });
}

/**
 * useRegisterTrustedEnrollmentIssuerMutation registers a new trusted enrollment
 * issuer. The backend forces enabled=true and re-validates jwks_uri; on success
 * it returns the created record directly (201).
 */
export function useRegisterTrustedEnrollmentIssuerMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<TrustedEnrollmentIssuer, ApiError, CreateTrustedEnrollmentIssuerInput>({
    mutationFn: (input) => {
      const body = createTrustedEnrollmentIssuerSchema.parse(input);
      return apiFetch<unknown>(base(tenantID), {
        method: "POST",
        body: {
          display_name: body.display_name,
          issuer: body.issuer,
          jwks_uri: body.jwks_uri,
          audience: body.audience,
          return_url_allowlist: body.return_url_allowlist,
        },
      }).then((raw) => trustedEnrollmentIssuerSchema.parse(raw));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.trustedEnrollmentIssuers.all });
    },
  });
}

/**
 * useDeleteTrustedEnrollmentIssuerMutation permanently removes a trusted
 * enrollment issuer (DELETE by id → 204). Hard delete; disabling via update
 * (enabled=false) is the reversible alternative. Platform-admin gated and not
 * step-up-gated, so a plain apiFetch; invalidates the list on success.
 */
export function useDeleteTrustedEnrollmentIssuerMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (issuerID) =>
      apiFetch<void>(`${base(tenantID)}/${encodeURIComponent(issuerID)}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.trustedEnrollmentIssuers.all });
    },
  });
}

/**
 * useUpdateTrustedEnrollmentIssuerMutation edits an existing trusted enrollment
 * issuer (PUT by id). Like register it is platform-admin gated and NOT
 * step-up-gated, so it is a plain apiFetch. The backend re-validates jwks_uri
 * and return URLs; on success it returns the updated record (200).
 */
export function useUpdateTrustedEnrollmentIssuerMutation(
  tenantID: string,
  issuerID: string,
) {
  const qc = useQueryClient();
  return useMutation<TrustedEnrollmentIssuer, ApiError, UpdateTrustedEnrollmentIssuerInput>({
    mutationFn: (input) => {
      const body = updateTrustedEnrollmentIssuerSchema.parse(input);
      return apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(issuerID)}`, {
        method: "PUT",
        body: {
          display_name: body.display_name,
          issuer: body.issuer,
          jwks_uri: body.jwks_uri,
          audience: body.audience,
          return_url_allowlist: body.return_url_allowlist,
          enabled: body.enabled,
        },
      }).then((raw) => trustedEnrollmentIssuerSchema.parse(raw));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.trustedEnrollmentIssuers.all });
    },
  });
}
